import { useEffect, useRef, useState, useCallback } from 'react'
import { io } from 'socket.io-client'
import { toast } from 'react-toastify'
import MailIcon from '@mui/icons-material/Mail'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import apiClient from '../../api/apiClient'

const UNREAD_EMAIL_BASE_KEY = 'unread_email_count'

const ACCESS_TOKEN_KEY = process.env.REACT_APP_ACCESS_TOKEN_KEY || 'accessToken'
const REFRESH_TOKEN_KEY = process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refreshToken'

const useSocketNotifications = (user) => {
  const socketRef = useRef(null)
  const userId = user?.id
  const storageKey = userId ? `${UNREAD_EMAIL_BASE_KEY}_${userId}` : null

  const [unReadEmail, setUnreadEmail] = useState(() => {
    if (!storageKey) return 0
    try {
      const savedCount = localStorage.getItem(storageKey)
      return savedCount ? parseInt(savedCount, 10) : 0
    } catch {
      return 0
    }
  })

  const setUnreadEmailAndPersist = useCallback(
    (count) => {
      setUnreadEmail(count)
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, count.toString())
        } catch (error) {}
      }
    },
    [storageKey]
  )
  const getValidToken = async () => {
    let token = sessionStorage.getItem(ACCESS_TOKEN_KEY)
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY)

    if (!token && refreshToken) {
      try {
        const rs = await apiClient.post('/refreshToken', { refreshToken })
        token = rs.data.accessToken
        sessionStorage.setItem(ACCESS_TOKEN_KEY, token)
      } catch (err) {
        handleLogout()
        return null
      }
    }
    return token
  }

  const handleLogout = () => {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
    if (storageKey) {
      localStorage.removeItem(storageKey)
    }
    if (typeof window !== 'undefined') window.location.href = '/login'
  }

  useEffect(() => {
    if (!user) {
      setUnreadEmail(0)
      return
    }

    let socket = null

    const connectSocket = async () => {
      const token = await getValidToken()
      if (!token) return

      socket = io(process.env.REACT_APP_WS_URL, {
        path: '/notif/socket.io/',
        transports: ['websocket'],
        auth: { token },
        timeout: 5000,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000
      })

      socketRef.current = socket

      const handleNewEmail = (email) => {
        toast.info(
          <div>
            <p>{email.message}</p> <p>De: {email.data.from}</p>
            <p>{email.data.subject}</p>
          </div>,
          { icon: () => <MailIcon />, autoClose: 10000 }
        )
      }

      const handleUnreadEmail = (email) => setUnreadEmailAndPersist(email.data)

      const handleNewReminder = (reminder) => {
        toast.info(
          <div>
            <p>{reminder.message}</p> <p>{reminder.data.title}</p>
          </div>,
          { icon: () => <CalendarTodayIcon />, autoClose: 10000 }
        )
      }

      const events = {
        'new-email': handleNewEmail,
        'unread-email': handleUnreadEmail,
        'new-reminder': handleNewReminder
      }

      socket.on('connect', () => {
        socket.emit('username', user.email.toLowerCase())
        Object.entries(events).forEach(([event, handler]) => socket.on(event, handler))
      })

      socket.on('connect_error', async (err) => {
        if (err.message === 'jwt expired') {
          const newToken = await getValidToken()
          if (newToken && socket) {
            socket.auth.token = newToken
            socket.connect()
          }
        }
      })

      socket.on('disconnect', (reason) => {})
    }

    connectSocket()

    return () => {
      if (socket) {
        Object.entries({
          'new-email': () => {},
          'unread-email': () => {},
          'new-reminder': () => {}
        }).forEach(([event, handler]) => socket.off(event, handler))
        socket.disconnect()
      }
    }
  }, [user, getValidToken, setUnreadEmailAndPersist, handleLogout])

  return { unReadEmail }
}

export default useSocketNotifications
